import React from 'react'

import { Table } from '@mantine/core'
import { ChevronUp, ChevronDown, ChevronsUpDown } from 'lucide-react'

import { cn } from 'src/utils'

interface TableHeaderProps {
  children: React.ReactNode
  className?: string
  isSortable?: boolean
  sortDirection?: 'asc' | 'desc' | null
  onSort?: () => void
  enableResize?: boolean
  onStartResize?: (e: React.MouseEvent) => void
  isLastColumn?: boolean
}

const TableHeader = ({
  children,
  className,
  isSortable = false,
  sortDirection = null,
  onSort = () => {},
  enableResize = false,
  onStartResize = (_e) => {},
  isLastColumn = false,
  ...props
}: TableHeaderProps &
  Omit<React.ComponentProps<typeof Table.Th>, 'children'>) => {
  const getSortIcon = () => {
    if (!isSortable) return null

    if (sortDirection === 'asc') {
      return <ChevronUp size={14} />
    } else if (sortDirection === 'desc') {
      return <ChevronDown size={14} />
    } else {
      return <ChevronsUpDown size={14} />
    }
  }

  return (
    <Table.Th
      className={cn(
        'relative bg-doubleNickel-gray-50 p-2 text-xs font-semibold text-doubleNickel-gray-700',
        isSortable && 'cursor-pointer hover:bg-doubleNickel-gray-100',
        enableResize &&
          !isLastColumn &&
          'border-r border-doubleNickel-gray-300',
        className
      )}
      onClick={isSortable ? onSort : undefined}
      {...props}
    >
      <div className="flex items-center justify-between">
        <span className="truncate pr-1">{children}</span>
        {getSortIcon()}
      </div>
      {enableResize && !isLastColumn && (
        <div
          className="absolute right-0 top-0 h-full w-2 cursor-col-resize"
          role="button"
          tabIndex={0}
          onMouseDown={(e) => {
            e.stopPropagation()
            onStartResize(e)
          }}
          onKeyDown={(e) => {
            if (e.key === 'Enter' || e.key === ' ') {
              e.preventDefault()
              // Could implement keyboard resizing here
            }
          }}
        />
      )}
    </Table.Th>
  )
}

export default TableHeader
